// docs for router https://github.com/thepassle/app-tools/blob/master/router/README.md

import { html } from 'lit';

// Polyfill URLPattern if missing (Safari < 16.4)
// Use a type guard instead of any cast for lint compliance.
const g = globalThis as { URLPattern?: typeof URLPattern };
if (!g.URLPattern) {
  await import('urlpattern-polyfill');
}

import { Router } from '@thepassle/app-tools/router.js';
import { WERS_SURFACES_HIDDEN } from './modules/product-registry/wers-visibility.js';

import { scrollToTop } from '@thepassle/app-tools/router/plugins/scrollToTop.js';
import { checkServiceWorkerUpdate } from '@thepassle/app-tools/router/plugins/checkServiceWorkerUpdate.js';
// @ts-ignore
import { title } from '@thepassle/app-tools/router/plugins/title.js';

import './pages/app-home/app-home.js';
import './pages/app-profile/app-profile.js';
import './pages/app-about/app-about.js';
import './pages/app-settings/app-settings.js'; // Updated path

import './pages/app-login-internal/app-login-internal.js';
import './pages/app-notallowed/app-notallowed.js';
import './pages/app-members/app-members.js';
import './pages/app-members/company-create-page.js';
import './pages/app-members/workspace/member-workspace.js';
import './pages/app-members/site-workspace/site-workspace.js';
import './pages/app-members/view-sites.js';
import './pages/app-settings/user-management/staff-list.js';
import './pages/app-settings/user-management/user-edit.js';
import './pages/app-settings/user-management/user-add.js';
import './pages/app-settings/security-audit-log.js';
import './pages/app-settings/state-colours/state-colours-page.js';
import './pages/app-settings/email-audit-log.js';
import './pages/app-settings/activity-dashboard.js';
//add all pages as imports here

import './pages/modules-hub/modules-hub.js';
import './pages/workflow-menu/workflow-menu.js';

// Feature-module pages (product-registry, glass-accreditation, gdc,
// workflow-sandbox) are lazy-loaded on first navigation into the module - see
// lazyModulesPlugin - so each module compiles into its own chunk instead of the
// main app bundle.
import './modules/experimental/pages/prod-test-thumb-sandbox.js';
import {
  CERT_TYPE_AS2208_LABEL,
  CERT_TYPE_AS4666_LABEL,
} from './modules/glass-accreditation/utils/certificate-utils';
import { activeUserProfile } from './active-userprofile';
import type {
  Context,
  Plugin as RoutePlugin,
} from '@thepassle/app-tools/router.js';
import { canAccessRoute } from './utility/route-access.js';
import { findModuleByRoute } from './modules/registry/module-registry.js';
import { recordModuleUsage } from './services/recent-modules-service.js';
import { terms } from './language/terms';

// Vite injects env via import.meta.env; cast only that piece locally.
const viteEnv = (import.meta as unknown as { env: { BASE_URL: string } }).env;
const baseURL: string = viteEnv.BASE_URL;

const isLoggedIn = () => {
  const user = activeUserProfile();
  return !!user && (user.isLoggedIn ?? false);
};

/**
 * Unified route access plugin that uses route-access.ts rules.
 * This is the primary access control mechanism - routes should use this
 * instead of individual feature/domain/role plugins.
 */
const routeAccessPlugin: RoutePlugin = {
  name: 'route-access',
  shouldNavigate: (context: Context) => {
    // Extract the path from context
    const path = context.url?.pathname ?? '/';
    const user = activeUserProfile();

    const result = canAccessRoute(path, user?.access, isLoggedIn());

    if (result.allowed) {
      // Redirect is ignored when condition returns true
      return { redirect: '', condition: () => true };
    }

    // Determine redirect based on reason
    const redirect =
      result.reason === 'not-logged-in'
        ? resolveRouterPath('login')
        : resolveRouterPath('notallowed');

    return {
      redirect,
      condition: () => false,
    };
  },
};

/**
 * Plugin to track module usage for the "Recent" section on the home page.
 * Records module visits in IndexedDB per user.
 */
const recentModulesTrackingPlugin: RoutePlugin = {
  name: 'recent-modules-tracking',
  afterNavigation: ({ url }) => {
    const path = url?.pathname ?? '/';
    // Strip leading slash and check if this path corresponds to a known module
    const routePath = path.startsWith('/') ? path.slice(1) : path;

    // Skip home page - it should never appear in recent modules
    if (!routePath || routePath === '') return;

    const module = findModuleByRoute(routePath);
    if (!module) return;

    const user = activeUserProfile();
    if (!user?.userId) return;

    // Fire and forget - don't await to avoid blocking navigation
    recordModuleUsage(module.id, user.userId).catch(() => {
      // Silent fail - tracking is non-critical
    });
  },
};

/**
 * Path prefix -> the feature module's page barrel. product-registry /
 * glass-accreditation / gdc live under /module/<id>/; the workflow sandbox lives
 * under /sandbox/workflows. Each barrel is a separate chunk, loaded on demand.
 */
const moduleLoaders: ReadonlyArray<readonly [string, () => Promise<unknown>]> =
  [
    [
      'module/product-registry',
      () => import('./modules/product-registry/product-registry-pages.js'),
    ],
    [
      'module/glass-accreditation',
      () =>
        import('./modules/glass-accreditation/glass-accreditation-pages.js'),
    ],
    ['module/gdc', () => import('./modules/gdc/gdc-pages.js')],
    [
      'sandbox/workflows',
      () => import('./modules/workflow-sandbox/workflow-sandbox-pages.js'),
    ],
  ];

/**
 * Lazy-loads a feature module's page bundle the first time one of its routes is
 * navigated to, so each module compiles into its own chunk rather than the main
 * app bundle. Awaited (the router awaits beforeNavigation): the module's page
 * elements are guaranteed defined before the route renders, so there is no
 * undefined-element window even on a cold deep-link.
 */
const lazyModulesPlugin: RoutePlugin = {
  name: 'lazy-modules',
  // The router `await`s beforeNavigation (see @thepassle/app-tools
  // router/index.js: `await plugin?.beforeNavigation?.(...)`), so this async
  // loader resolves before the route renders. The upstream Plugin type
  // under-declares beforeNavigation as `() => void`, which trips
  // no-misused-promises; the await is intentional and correct here.
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  beforeNavigation: async ({ url }) => {
    const path = url?.pathname ?? '';
    for (const [prefix, load] of moduleLoaders) {
      if (path.includes(prefix)) {
        await load();
        break;
      }
    }
  },
};

export let router: Router | undefined = undefined;

export function createRouter() {
  router = new Router({
    plugins: [
      /** Checks for service worker updates on route navigations */
      checkServiceWorkerUpdate,
      // scrollToTop scrolls the window, which no longer scrolls (D-096); app-index scrolls
      // and focuses the route element itself after each render, and announces the title,
      // which is what the resetFocus plugin used to do from a hidden div in the body - a div
      // that then held the focus the route element needs for page keys.
      scrollToTop,
      /** Unified route access control - uses rules from route-access.ts */
      routeAccessPlugin,
      /** Track recently used modules for home page display */
      recentModulesTrackingPlugin,
      /** Lazy-load feature-module page bundles on first navigation into them */
      lazyModulesPlugin,
    ],
    routes: [
      {
        path: resolveRouterPath(),
        title: 'Home',
        render: () => html`<app-home></app-home>`,
      },
      {
        path: resolveRouterPath('about'),
        title: 'About',
        render: () => html`<app-about></app-about>`,
      },
      {
        path: resolveRouterPath('user/profile'),
        title: 'Profile',
        render: () => html`<app-profile></app-profile>`,
      },
      {
        path: resolveRouterPath('settings'),
        title: 'Settings',
        render: () => html`<app-settings></app-settings>`,
      },
      {
        // Tab id is a route segment per the ui-tabbed-pages rule; the bare URL is the
        // default (shared-labels) tab.
        path: resolveRouterPath('settings/badge-colours/:tab?'),
        title: 'Badge Colours',
        render: ({ params }) =>
          html`<state-colours-page .tab=${params.tab}></state-colours-page>`,
      },
      {
        path: resolveRouterPath('modules'),
        title: 'Modules',
        render: () => html`<modules-hub></modules-hub>`,
      },
      {
        // The workflow menu, live BESIDE the modules hub rather than replacing it, so the old one
        // can be retired once this is preferred rather than in the same breath as building it.
        path: resolveRouterPath('menu'),
        title: 'Menu',
        render: () => html`<workflow-menu></workflow-menu>`,
      },
      {
        path: resolveRouterPath('module/staff'),
        title: `${terms.agwa.value} ${terms.staffUser.value}`,
        render: () => html`<staff-list></staff-list>`,
      },
      {
        path: resolveRouterPath('module/audit-log'),
        title: 'Security Audit Log',
        render: () => html`<security-audit-log></security-audit-log>`,
      },
      {
        path: resolveRouterPath('module/user-activity'),
        title: `${terms.loginUser.value} Activity`,
        render: () => html`<activity-dashboard></activity-dashboard>`,
      },
      {
        path: resolveRouterPath('module/email-audit'),
        title: 'Email History',
        render: () => html`<email-audit-log></email-audit-log>`,
      },
      {
        path: resolveRouterPath('module/glass-accreditation/settings/:tab?'),
        title: `${terms.glassAccreditation.value} Settings`,
        render: ({ params }) =>
          html`<accreditation-settings
            .tab=${params.tab ?? 'safety-glass-types'}
          ></accreditation-settings>`,
      },
      {
        path: resolveRouterPath('module/glass-accreditation/certificates'),
        title: `${terms.glassAccreditation.value} Certificates`,
        render: () => html`<certificate-list-page></certificate-list-page>`,
      },
      {
        path: resolveRouterPath(
          'module/glass-accreditation/certificates/expired-no-successor'
        ),
        title: `${terms.glassAccreditation.value} Certificates`,
        render: () =>
          html`<certificate-list-page
            presetView="expired-no-successor"
          ></certificate-list-page>`,
      },
      {
        path: resolveRouterPath(
          'module/glass-accreditation/certificates/expiring-soon'
        ),
        title: `${terms.glassAccreditation.value} Certificates`,
        render: () =>
          html`<certificate-list-page
            presetView="expiring-soon"
          ></certificate-list-page>`,
      },
      {
        path: resolveRouterPath(
          'module/glass-accreditation/certificates/needs-audit'
        ),
        title: `${terms.glassAccreditation.value} Certificates`,
        render: () =>
          html`<certificate-list-page
            presetView="needs-audit"
          ></certificate-list-page>`,
      },
      {
        path: resolveRouterPath(
          'module/glass-accreditation/certificates/my-drafts'
        ),
        title: `${terms.glassAccreditation.value} Certificates`,
        render: () =>
          html`<certificate-list-page
            presetView="my-drafts"
          ></certificate-list-page>`,
      },
      {
        path: resolveRouterPath(
          'module/glass-accreditation/certificates/review-due'
        ),
        title: `${terms.glassAccreditation.value} Certificates`,
        render: () =>
          html`<certificate-list-page
            presetView="review-due"
          ></certificate-list-page>`,
      },
      {
        path: resolveRouterPath('module/glass-accreditation/companies'),
        title: `${terms.primeGlassManufacturer.value} ${terms.loginGroup.plural}`,
        render: () => html`<companies-list-page></companies-list-page>`,
      },
      {
        path: resolveRouterPath(
          'module/glass-accreditation/company/:companyId'
        ),
        title: `${terms.loginGroup.value} Certificates`,
        render: ({ params, query }) =>
          html`<company-certificates-page
            .companyId=${params.companyId}
            .siteId=${query.site ?? ''}
          ></company-certificates-page>`,
      },
      {
        path: resolveRouterPath('module/glass-accreditation/as2208/create'),
        title: `Create ${CERT_TYPE_AS2208_LABEL} Certificate`,
        render: () => html`<as2208-create-page></as2208-create-page>`,
      },
      {
        // Tab id is a route segment (CLAUDE.md, tabbed pages), so a refresh and a deep link land
        // on the tab the URL names; the bare URL is the default tab, which keeps every existing
        // link and the create flow's `?edit=true` landing where they always did.
        path: resolveRouterPath(
          'module/glass-accreditation/as2208/:certId/:tab?'
        ),
        title: `${CERT_TYPE_AS2208_LABEL} Certificate`,
        render: ({ params }) =>
          html`<as2208-detail-page
            .certId=${params.certId}
            .tab=${params.tab ?? ''}
          ></as2208-detail-page>`,
      },
      {
        path: resolveRouterPath('module/glass-accreditation/as4666/create'),
        title: `Create ${CERT_TYPE_AS4666_LABEL} Certificate`,
        render: () => html`<as4666-create-page></as4666-create-page>`,
      },
      {
        // Tab id is a route segment (CLAUDE.md, tabbed pages), so a refresh and a deep link land
        // on the tab the URL names; the bare URL is the default tab, which keeps every existing
        // link and the create flow's `?edit=true` landing where they always did.
        path: resolveRouterPath(
          'module/glass-accreditation/as4666/:certId/:tab?'
        ),
        title: `${CERT_TYPE_AS4666_LABEL} Certificate`,
        render: ({ params }) =>
          html`<as4666-detail-page
            .certId=${params.certId}
            .tab=${params.tab ?? ''}
          ></as4666-detail-page>`,
      },
      {
        path: resolveRouterPath('module/product-registry'),
        title: terms.productRegistry.value,
        render: () => html``,
        plugins: [
          {
            name: 'redirect-product-registry-root',
            shouldNavigate: () => ({
              redirect: resolveRouterPath('module/product-registry/products'),
              condition: () => false,
            }),
          },
        ],
      },
      {
        path: resolveRouterPath('module/product-registry/settings/:tab?'),
        title: `${terms.productRegistry.value} Settings`,
        render: ({ params }) =>
          html`<product-registry-settings-page
            .tab=${params.tab ?? 'operating-types'}
          ></product-registry-settings-page>`,
      },
      {
        path: resolveRouterPath('module/product-registry/products'),
        title: terms.productRegistry.value,
        render: () =>
          html`<product-registry-products-page></product-registry-products-page>`,
      },
      {
        path: resolveRouterPath('module/product-registry/products/new'),
        title: `Create ${terms.product.value}`,
        render: ({ query }) =>
          html`<product-registry-create-page
            .ownerId=${query.owner ?? ''}
          ></product-registry-create-page>`,
      },
      {
        path: resolveRouterPath(
          'module/product-registry/products/:productId/:tab?'
        ),
        title: `${terms.product.value} Detail`,
        render: ({ params }) =>
          html`<product-registry-product-page
            .productId=${params.productId}
            .tab=${params.tab ?? ''}
          ></product-registry-product-page>`,
      },
      {
        path: resolveRouterPath('module/product-registry/reports'),
        title: terms.productTestReport.plural,
        // `?new=bushfire` (the menu's quick link) opens the list with the bushfire product
        // picker showing - a bushfire report is product-scoped and has no /new of its own.
        render: ({ query }) =>
          html`<product-registry-reports-page
            .newReportType=${query.new ?? ''}
          ></product-registry-reports-page>`,
      },
      {
        path: resolveRouterPath(
          'module/product-registry/products/:productId/reports/as2047/new'
        ),
        title: 'New AS 2047 Report',
        render: ({ params }) =>
          html`<product-registry-as2047-create-page
            .productId=${params.productId}
          ></product-registry-as2047-create-page>`,
      },
      {
        path: resolveRouterPath(
          'module/product-registry/products/:productId/reports/as5218/new'
        ),
        title: 'New AS 5218 Report',
        render: ({ params }) =>
          html`<product-registry-as5218-create-page
            .productId=${params.productId}
          ></product-registry-as5218-create-page>`,
      },
      {
        path: resolveRouterPath(
          'module/product-registry/products/:productId/reports/bushfire/new'
        ),
        title: 'New AS 1530 Bushfire Report',
        render: ({ params }) =>
          html`<product-registry-bushfire-create-page
            .productId=${params.productId}
          ></product-registry-bushfire-create-page>`,
      },
      // The three WERS / Base Therm routes are withheld while WERS_SURFACES_HIDDEN holds
      // (D-087): a deep link to a hidden surface must miss like any unknown route, not open
      // a page the product page no longer offers.
      ...(WERS_SURFACES_HIDDEN
        ? []
        : [
            {
              path: resolveRouterPath(
                'module/product-registry/products/:productId/wers/new'
              ),
              title: 'New WERS Link',
              render: ({ params }) =>
                html`<product-registry-wers-create-page
                  .productId=${params.productId}
                ></product-registry-wers-create-page>`,
            },
            {
              path: resolveRouterPath(
                'module/product-registry/products/:productId/wers/:wersLinkId/batches/new'
              ),
              title: 'New Base Therms Batch',
              render: ({ params }) =>
                html`<product-registry-batch-create-page
                  .productId=${params.productId}
                  .wersLinkId=${params.wersLinkId}
                ></product-registry-batch-create-page>`,
            },
            {
              path: resolveRouterPath(
                'module/product-registry/products/:productId/wers/:wersLinkId/batches/:batchId'
              ),
              title: 'Base Therms Batch',
              render: ({ params }) =>
                html`<product-registry-batch-edit-page
                  .productId=${params.productId}
                  .wersLinkId=${params.wersLinkId}
                  .batchId=${params.batchId}
                ></product-registry-batch-edit-page>`,
            },
          ]),
      {
        path: resolveRouterPath('module/product-registry/reports/as2047/new'),
        title: 'New AS 2047 Report',
        render: () =>
          html`<product-registry-as2047-create-page></product-registry-as2047-create-page>`,
      },
      {
        path: resolveRouterPath('module/product-registry/reports/as5218/new'),
        title: 'New AS 5218 Report',
        render: () =>
          html`<product-registry-as5218-create-page></product-registry-as5218-create-page>`,
      },
      {
        path: resolveRouterPath(
          'module/product-registry/reports/as2047/:reportId/:tab?'
        ),
        title: 'AS 2047 Report',
        render: ({ params }) =>
          html`<product-registry-as2047-page
            .reportId=${params.reportId}
            .tab=${params.tab ?? ''}
          ></product-registry-as2047-page>`,
      },
      {
        path: resolveRouterPath(
          'module/product-registry/reports/as5218/:reportId/:tab?'
        ),
        title: 'AS 5218 Report',
        render: ({ params }) =>
          html`<product-registry-as5218-page
            .reportId=${params.reportId}
            .tab=${params.tab ?? ''}
          ></product-registry-as5218-page>`,
      },
      {
        path: resolveRouterPath(
          'module/product-registry/reports/bushfire/:reportId/:tab?'
        ),
        title: 'AS 1530 Bushfire Report',
        render: ({ params }) =>
          html`<product-registry-bushfire-page
            .reportId=${params.reportId}
            .tab=${params.tab ?? ''}
          ></product-registry-bushfire-page>`,
      },
      // ─────────────────────────────────────────────────────────────────────
      // Workflow Sandbox
      // ─────────────────────────────────────────────────────────────────────
      {
        path: resolveRouterPath('sandbox/workflows'),
        title: 'Workflow Sandbox',
        render: () => html`<sandbox-workflow-list></sandbox-workflow-list>`,
      },
      {
        path: resolveRouterPath('sandbox/workflows/new'),
        title: 'New Workflow',
        render: () => html`<sandbox-workflow-editor></sandbox-workflow-editor>`,
      },
      {
        path: resolveRouterPath('sandbox/workflows/:id'),
        title: 'Edit Workflow',
        // The id is handed in on every navigation: moving from one workflow to another keeps the
        // same editor element, which then switches to the new id (#410 u7, @WS-CONTENT-004/005).
        render: ({ params }) =>
          html`<sandbox-workflow-editor
            .workflowId=${params.id}
          ></sandbox-workflow-editor>`,
      },
      // ─────────────────────────────────────────────────────────────────────
      // Experimental
      // ─────────────────────────────────────────────────────────────────────
      {
        path: resolveRouterPath('experimental/prod-test-thumb-generator'),
        title: `${terms.product.value} Test Thumbnail Sandbox`,
        render: () => html`<prod-test-thumb-sandbox></prod-test-thumb-sandbox>`,
      },
      {
        path: resolveRouterPath('settings/staff/edit/:userId'),
        title: `Edit ${terms.loginUser.value}`,
        render: ({ params, query }) => {
          return html`<user-edit
            .userId=${params.userId}
            .groupId=${query.groupId}
            .returnUrl=${query.returnUrl ?? ''}
          ></user-edit>`;
        },
      },
      {
        path: resolveRouterPath('settings/staff/add'),
        title: `Add ${terms.loginUser.value}`,
        render: ({ query }) => {
          return html`<user-add
            .groupId=${query.groupId}
            .returnUrl=${query.returnUrl ?? ''}
          ></user-add>`;
        },
      },

      {
        path: resolveRouterPath('login'),
        title: 'Login',
        render: () => html`<app-login-internal></app-login-internal>`,
        plugins: [
          {
            name: 'redirect-if-logged-in',
            shouldNavigate: (context: Context) => {
              // Allow adding another account even if already logged in
              const isAddingAccount = context.query['addAccount'] === 'true';
              return {
                redirect: resolveRouterPath(),
                condition: () => !isLoggedIn() || isAddingAccount,
              };
            },
          },
        ],
      },
      {
        path: resolveRouterPath('module/gdc'),
        title: 'Documents',
        render: () => html`<gdc-module></gdc-module>`,
      },
      {
        path: resolveRouterPath('module/gdc/create'),
        title: 'Create Document',
        render: () => html`<gdc-document-create></gdc-document-create>`,
      },
      {
        path: resolveRouterPath('module/gdc/:id'),
        title: 'View Document',
        render: ({ params }) =>
          html`<gdc-document-view
            data-doc-id="${params.id}"
          ></gdc-document-view>`,
      },
      {
        path: resolveRouterPath('maintenance/companies'),
        title: `${terms.loginGroup.value} Management`,
        render: () => html`<app-members></app-members>`,
      },
      {
        // Registered before the :groupId workspace routes below so the literal 'new' is
        // never captured as a group id.
        path: resolveRouterPath('maintenance/companies/new'),
        title: `Add ${terms.loginGroup.value}`,
        render: () => html`<company-create-page></company-create-page>`,
      },
      {
        path: resolveRouterPath('maintenance/sites'),
        title: `${terms.loginSite.value} Management`,
        render: () => html`<view-sites></view-sites>`,
      },
      {
        path: resolveRouterPath(
          'maintenance/companies/:groupId/sites/:siteId/:tabId?'
        ),
        title: `${terms.loginSite.value} Workspace`,
        render: ({ params }) =>
          html`<site-workspace
            .groupId=${params.groupId}
            .siteId=${params.siteId}
            .tab=${params.tabId ?? 'overview'}
          ></site-workspace>`,
      },
      {
        path: resolveRouterPath('maintenance/companies/:groupId/:tabId?'),
        title: `${terms.loginGroup.value} Workspace`,
        render: ({ params }) =>
          html`<member-workspace
            .groupId=${params.groupId}
            .tab=${params.tabId ?? 'overview'}
          ></member-workspace>`,
      },
      {
        path: resolveRouterPath('notallowed'),
        title: 'Access Denied',
        render: () => html`<app-notallowed></app-notallowed>`,
      },
    ],
  });
}
// This function will resolve a path with whatever Base URL was passed to the vite build process.
// Use of this function throughout the starter is not required, but highly recommended, especially if you plan to use GitHub Pages to deploy.
// If no arg is passed to this function, it will return the base URL.

export function resolveRouterPath(unresolvedPath?: string) {
  let resolvedPath = baseURL;
  if (unresolvedPath) {
    resolvedPath = resolvedPath + unresolvedPath;
  }

  return resolvedPath;
}
